import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {
    isCurveGeometryAttributeData,
    isCurveGeometryAttributeSetData,
    isGeometryAttributeData,
    isMeshGeometryAttributeData,
    isMeshGeometryAttributeSetData,
    isPointGeometryAttributeData,
    isPointGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {StringLike} from "#template/nodes/types"
import {stringLike} from "#template/nodes/string/fundamental"
export type GetAttributeParameters<TAttributeSet> = {
    name: string
    attributeName: StringLike | null
    attributeSet: TAttributeSet | null
}

export const getAttributeParametersSchema = <TAttributeSet>(attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>) =>
    z.object({
        name: z.string(),
        attributeName: z.nullable(stringLike),
        attributeSet: z.nullable(attributeSetSchema),
    })

function getAttributeDataFromAttributeSetData<T extends IndexingDim>(
    attributeName: string,
    attributeSetData: GeometryAttributeSetData<T>,
): GeometryAttributeData<T> | null {
    if (attributeSetData.data[attributeName] === undefined) return null

    if (isPointGeometryAttributeSetData(attributeSetData)) {
        const value = attributeSetData.data[attributeName]
        if (isGeometryAttributeData(value) && isPointGeometryAttributeData(value)) {
            return value
        }
        return null
    } else if (isCurveGeometryAttributeSetData(attributeSetData)) {
        const value = attributeSetData.data[attributeName]
        if (isGeometryAttributeData(value) && isCurveGeometryAttributeData(value)) {
            return value
        }
        return null
    } else if (isMeshGeometryAttributeSetData(attributeSetData)) {
        const value = attributeSetData.data[attributeName]
        if (isGeometryAttributeData(value) && isMeshGeometryAttributeData(value)) {
            return value
        }
        return null
    }
    return null
}

export abstract class GeometryGetAttributeNode<
    out T extends IndexingDim,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeNode<T, GetAttributeParameters<TAttributeSet>> {
    protected override hasValidParameters(
        parameters: GetAttributeParameters<TAttributeSet>,
    ): parameters is GetAttributeParameters<TAttributeSet> & NonNullableParameters<GetAttributeParameters<TAttributeSet>> {
        return parameters.attributeSet !== null && parameters.attributeName !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<GetAttributeParameters<TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attributeSet, attributeName} = parameters

        const [evaluatedAttributeNameValid, evaluatedAttributeNameInvalid] = scope.branch(evaluator.evaluate(attributeName))
        const [evaluatedAttributeSetValid, evaluatedAttributeSetInvalid] = scope.branch(evaluator.evaluate(attributeSet))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedAttributeNameValid, evaluatedAttributeSetValid),
            ([attributeName, attributeSet]) => getAttributeDataFromAttributeSetData(attributeName, attributeSet),
            "getAttributeDataFromAttributeSetData",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: scope.phi(evaluatedAttributeSetInvalid, evaluatedAttributeNameInvalid)}
    }
}
